import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, axiosInstancePython } from "../../utils/axiosInstance";
import {
  PythonTopResumeAndMatch,
  ScheduleInterviewEndPoint,
  ScheduleProgrammingTestEndPoint,
} from "../../api-routes";

const scheduleInterview = async (formData) => {
  return (
    await axiosInstance.post(
      ScheduleInterviewEndPoint.SCHEDULE_INTERVIEW,
      formData,
    )
  ).data;
};
const uploadAudio = async (formData) => {
  return (
    await axiosInstance.post(ScheduleInterviewEndPoint.UPLOAD_AUDIO, formData)
  ).data;
};
const getAllInterview = async ({
  page = 1,
  limit = 5,
  status = "",
  search = "",
  startDate = "",
  endDate = "",
  interviewType = "All",
}) => {
  let paramsString =
    status === "All"
      ? `?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      : `?page=${page}&limit=${limit}&status=${encodeURIComponent(status)}&search=${encodeURIComponent(search)}`;

  if (interviewType && interviewType !== "All") {
    paramsString += `&interviewType=${encodeURIComponent(interviewType)}`;
  }

  if (startDate) {
    paramsString += `&startTime[gte]=${startDate}`;
  }
  if (endDate) {
    paramsString += `&startTime[lte]=${endDate}`;
  }

  return (
    await axiosInstance.get(
      ScheduleInterviewEndPoint.GET_ALL_INTERVIEW_SCHEDULE + paramsString,
    )
  ).data;
};
const validateInterviews = async (data) => {
  return (
    await axiosInstance.post(
      ScheduleInterviewEndPoint.VALIDATE_INTERVIEW_SCHEDULE,
      data,
    )
  ).data;
};

const sendInterviewOTP = async (data) => {
  return (
    await axiosInstance.post(
      ScheduleInterviewEndPoint.SEND_INTERVIEW_OTP,
      data,
    )
  ).data;
};

const submitInterview = async (data) => {
  return (
    await axiosInstance.post(ScheduleInterviewEndPoint.SUBMIT_INTERVIEW, data)
  ).data;
};
const updateInterview = async (data) => {
  const { _id, formData } = data;

  return (
    await axiosInstance.put(
      ScheduleInterviewEndPoint.UPDATE_INTERVIEW + `/${_id}`,
      formData,
    )
  ).data;
};

const generateInterviewQuestions = async (data) => {
  return (
    await axiosInstance.post(
      ScheduleInterviewEndPoint.GENERATE_INTERVIEW_QUESTIONS,
      data,
    )
  ).data;
};

export const useScheduleInterview = () => {
  return useMutation({
    mutationFn: (data) => scheduleInterview(data),
  });
};
export const useUploadAudio = () => {
  return useMutation({
    mutationFn: (data) => uploadAudio(data),
  });
};

export const useAllInterview = ({ page, limit, status, search, startDate, endDate, interviewType }) => {
  return useQuery({
    queryKey: ["allInterview", { page, limit, status, search, startDate, endDate, interviewType }],
    queryFn: () =>
      getAllInterview({
        page,
        limit,
        status,
        search,
        startDate,
        endDate,
        interviewType,
      }),
    keepPreviousData: true,
  });
};

export const useValidateInterviews = () => {
  return useMutation({
    mutationFn: (data) => validateInterviews(data),
  });
};

export const useSubmitInterview = () => {
  return useMutation({
    mutationFn: (data) => submitInterview(data),
  });
};
export const useUpdateInterview = () => {
  return useMutation({
    mutationFn: (data) => updateInterview(data),
  });
};

export const useGenerateInterviewQuestions = () => {
  return useMutation({
    mutationFn: (data) => generateInterviewQuestions(data),
  });
};

const checkCandidateEmail = async (data) => {
  return (
    await axiosInstance.post(
      ScheduleInterviewEndPoint.CHECK_CANDIDATE_EMAIL,
      data,
    )
  ).data;
};

export const useCheckCandidateEmail = () => {
  return useMutation({
    mutationFn: (data) => checkCandidateEmail(data),
  });
};

const cancelInterviewById = async (id) => {
  const response = await axiosInstance.put(
    `${ScheduleInterviewEndPoint.CANCEL_INTERVIEW}/${id}`,
  );
  return response.data;
};

export const useCancelInterviewById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelInterviewById,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allInterview");
      console.log("Interview canceled:", data);
    },
    onError: (error) => {
      console.error("Error canceling interview:", error);
    },
  });
};
const cancelCombinedById = async ({ id, interviewType }) => {
  const isCodingType = interviewType === "Coding Test" || interviewType === "AI Bot +";
  const endpoint = isCodingType
    ? `${ScheduleProgrammingTestEndPoint.CANCEL_PROGRAMMING_TEST}/${id}`
    : `${ScheduleInterviewEndPoint.CANCEL_INTERVIEW}/${id}`;
  const response = await axiosInstance.put(endpoint);
  return response.data;
};

export const useCancelCombinedById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelCombinedById,
    onSuccess: () => {
      queryClient.invalidateQueries("allInterview");
    },
  });
};

export const combinedReportById = async ({ id, interviewType }) => {
  const isCodingType = interviewType === "Coding Test" || interviewType === "AI Bot +";
  const endpoint = isCodingType
    ? `${ScheduleProgrammingTestEndPoint.DOWNLOAD_TEST_REPORT}/${id}`
    : `${ScheduleInterviewEndPoint.GET_CANDIDATE_REPORT}/${id}`;
  const response = await axiosInstance.get(endpoint, { responseType: "blob" });
  const blob = response.data;
  if (blob && response.headers?.["x-report-generated"] === "true") {
    blob.wasGenerated = true;
  }
  return blob;
};

export const useCombinedReportById = () => {
  return useMutation({
    mutationFn: combinedReportById,
  });
};
const getAllInterviewReports = async ({
  page = 1,
  limit = 5,
  status = "",
  search = "",
  interviewType = "All",
}) => {
  let paramsString =
    status === "All"
      ? `?page=${page}&limit=${limit}&search=${search}`
      : `?page=${page}&limit=${limit}&status=${status}&search=${search}`;

  if (interviewType && interviewType !== "All") {
    paramsString += `&interviewType=${encodeURIComponent(interviewType)}`;
  }

  return (
    await axiosInstance.get(
      ScheduleInterviewEndPoint.GET_ALL_INTERVIEW_REPORTS + paramsString,
    )
  ).data;
};

export const useAllInterviewReports = ({ page, limit, status, search, interviewType }) => {
  return useQuery({
    queryKey: ["allInterviewReports", { page, limit, status, search, interviewType }],
    queryFn: () =>
      getAllInterviewReports({
        page,
        limit,
        status,
        search,
        interviewType,
      }),
    keepPreviousData: true,
  });
};

const fetchInterviewById = async (id) => {
  const response = await axiosInstance.get(
    `${ScheduleInterviewEndPoint.EDIT_SCHEDULE_INTERVIEW}/${id}`,
  );
  return response.data;
};

export const useInterviewById = (id) => {
  return useQuery({
    queryKey: ["interview", id],
    queryFn: () => fetchInterviewById(id),
    enabled: !!id,
    select: (data) => {
      if (data?.status === "completed" || data?.status === "cancelled") {
        return null;
      }
      return data;
    },
  });
};

const candidateReportById = async (id) => {
  const response = await axiosInstance.get(
    `${ScheduleInterviewEndPoint.GET_CANDIDATE_REPORT}/${id}`,
    { responseType: "blob" },
  );
  const blob = response.data;
  if (blob && response.headers?.["x-report-generated"] === "true") {
    blob.wasGenerated = true;
  }
  return blob;
};

export const useReportById = () => {
  return useMutation({
    mutationFn: (id) => candidateReportById(id),
  });
};

const blockInterviewById = async (id) => {
  const response = await axiosInstance.put(
    `${ScheduleInterviewEndPoint.BLOCKED_INTERVIEW}/${id}`,
  );
  return response.data;
};

export const useBlockInterviewMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: blockInterviewById,
    onSuccess: (data) => {
      queryClient.invalidateQueries("allInterviews");
      console.log("Interview canceled:", data);
    },
    onError: (error) => {
      console.error("Error canceling interview:", error);
    },
  });
};

// New Hook
const uploadReferenceVideo = async (formData) => {
  const res = await axiosInstancePython.post(
    PythonTopResumeAndMatch.UPLOAD_RESPONSE_VIDEO,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return res.data;
};

export const useUploadResponseVideo = () => {
  return useMutation({
    mutationFn: uploadReferenceVideo,
    onError: (err) => console.error("Reference video upload failed:", err),
  });
};

export const useSendInterviewOTP = () => {
  return useMutation({
    mutationFn: sendInterviewOTP,
    onError: (err) => console.error("Send OTP failed:", err),
  });
};
